import { ArrowRight } from "lucide-react";

const KPIS = [
  { label: "Non-contracted spend", value: "$15.9M", sub: "33% off-contract", color: "#ec1c8c", accent: true },
  { label: "Ad-hoc channel", value: "$12.8M", sub: "26.6%, highest leakage", color: "#ec1c8c", accent: true },
  { label: "Contracted", value: "67%", sub: "target 85%", color: "#14b8a6", accent: false },
  { label: "Addressable savings", value: "$1.59M", sub: "via preferred suppliers", color: "#f59e0b", accent: false },
];

interface Slice {
  label: string;
  pct: number;
  color: string;
}

const DIRECT_INDIRECT: Slice[] = [
  { label: "Direct · $37.6M", pct: 78, color: "#1e1b4b" },
  { label: "Indirect · $10.6M", pct: 22, color: "#a78bfa" },
];

const CONTRACTED: Slice[] = [
  { label: "Contracted · $32.3M", pct: 67, color: "#10b981" },
  { label: "Non-contracted · $15.9M", pct: 33, color: "#ec1c8c" },
];

const FLAGS = [
  { violation: "Non-contracted buys > $25K", count: "184", action: "Require PO + contract ID", tone: "teal" },
  { violation: "QC-failed receipts paid", count: "62", action: "Hold payment, supplier CAPA", tone: "orange" },
  { violation: "Ad-hoc channel buys", count: "1,240", action: "Re-route to panel / catalog", tone: "teal" },
];

function SplitDonut({ slices, centerLabel, centerValue }: { slices: Slice[]; centerLabel: string; centerValue: string }) {
  const r = 52;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg className="sd-donut sd-donut--md" viewBox="0 0 160 160" role="img" aria-label={`${centerLabel} ${centerValue}`}>
      {slices.map(({ label, pct, color }) => {
        const dash = (pct / 100) * circ;
        const rot = (offset / 100) * 360 - 90;
        offset += pct;
        return (
          <circle
            key={label}
            cx="80"
            cy="80"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth="26"
            strokeDasharray={`${dash} ${circ - dash}`}
            transform={`rotate(${rot} 80 80)`}
          />
        );
      })}
      <text x="80" y="76" textAnchor="middle" fontSize="9" fill="#9ca3af">{centerLabel}</text>
      <text x="80" y="94" textAnchor="middle" fontSize="16" fontWeight="700" fill="#1e1b4b">{centerValue}</text>
    </svg>
  );
}

function SplitCard({ title, slices, centerLabel }: { title: string; slices: Slice[]; centerLabel: string }) {
  return (
    <div className="si-card sm-card">
      <p className="sm-card__title">{title}</p>
      <div className="sd-geo sd-geo--split">
        <SplitDonut slices={slices} centerLabel={centerLabel} centerValue={`${slices[centerLabel === "Maverick" ? 1 : 0].pct}%`} />
        <div className="sd-geo__legend">
          {slices.map(({ label, pct, color }) => (
            <div key={label} className="sd-geo__item">
              <span className="sd-geo__swatch" style={{ background: color }} />
              <span className="sd-geo__label">{label}</span>
              <strong>{pct}%</strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function MaverickCompliancePanel() {
  return (
    <div className="sm-panel">
      <div className="sm-kpi-grid">
        {KPIS.map(({ label, value, sub, color, accent }) => (
          <div key={label} className="sm-kpi" style={{ borderColor: color }}>
            <p className="sm-kpi__label">{label}</p>
            <p className={`sm-kpi__value${accent ? " sm-kpi__value--accent" : ""}`}>{value}</p>
            <p className="sm-kpi__sub">{sub}</p>
          </div>
        ))}
      </div>

      <div className="si-grid-2">
        <SplitCard title="Direct vs Indirect" slices={DIRECT_INDIRECT} centerLabel="Direct" />
        <SplitCard title="Contracted vs Non-Contracted" slices={CONTRACTED} centerLabel="Maverick" />
      </div>

      <div className="si-card sm-card sm-card--flush">
        <div className="sm-card__head">
          <p className="sm-card__title">Compliance Flags</p>
          <span className="sm-tag">Policy violations</span>
        </div>
        <table className="sm-table">
          <thead>
            <tr>
              <th>Violation</th>
              <th className="sm-table__right">Count</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {FLAGS.map(({ violation, count, action, tone }) => (
              <tr key={violation}>
                <td className="sm-table__strong">{violation}</td>
                <td className="sm-table__right sm-table__accent sm-table__strong sd-count">{count}</td>
                <td className={`sd-action sd-action--${tone}`}>{action}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="si-card sm-card sm-card--flush sd-callout-card">
        <div className="sm-callout">
          <div className="sm-callout__body">
            <p className="sm-callout__eyebrow">From: Maverick &amp; Compliance</p>
            <p className="sm-callout__text">
              <strong>33% non-contracted spend plus a 26.6% ad-hoc channel is the single largest compliance leak.</strong>{" "}
              Default ad-hoc requests to panel/catalog and enforce no-PO/no-pay to capture ~$1.59M.
            </p>
          </div>
          <button type="button" className="sm-callout__btn sm-callout__btn--filled">
            Send to Opportunity
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
